/* eslint-disable no-param-reassign */
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  UIEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
  CircularProgress,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LocationContext, SuccessAlertContext } from "App";
import { Search } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AxiosError } from "axios";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { checkAccessTokenExpired } from "util/auth";
import OrderContainer from "../components/OrderContainer";
import OrderTable from "../components/OrderTable";
import OrderSearchDialog from "../components/OrderSearchDialog";
import useOrderSearch from "../hooks/useOrderSearch";
import {
  DestinationCheck,
  OrderConfirmErrorResponseApi,
  OrderConfirmParams,
  OrderForm,
  OrderGeneralSixBill,
  OrderSixBill,
  OrderStatus,
  OrderTyreSixBill,
  RegisterBillParam,
  SearchFormApiParams,
} from "../types";
import {
  convertUTCToDate,
  extractNoDupplicationSupplier,
  localeStringDisZero,
  orderStatusToNumber,
  removeEmptyValues,
} from "../utils/common";
import {
  MultiTypography,
  TableCellNarrow,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
} from "../components/TableCellSmall";
import { localEnv, hasRepresentativeCd, orderStatusOptionList } from "../constants";
import useOrderForm from "../hooks/useOrderForm";
import OrderEditForm from "../components/OrderEditForm";
import confirmOrderApi from "../api/confirmOrderApi";
import registerBillApi from "../api/registerBillApi";
import BackAlert from "../components/BackAlert";

const limit = 10;
const styles: { [key: string]: SxProps } = {
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "relative",
  },
  leftButtonContainer: {
    position: "absolute",
    top: 0,
    left: 0,
  },
  activeButton: {
    backgroundColor: "secondary.main",
    color: "var(--white)",
    "&:hover": {
      backgroundColor: "secondary.light",
    },
  },
  supplierTopCol: {
    // textAlign: 'center',
    p: 0.5,
    borderBottom: "none",
    height: 58,
  },
  supplierBottomCol: {
    // textAlign: 'center',
    p: 0.5,
    top: 67,
  },
};

/**
 * ヘッダーを返す
 * @param onBack 戻る
 * @param setIsOpenOrderSearch 検索モーダルを開く
 * @param setIsOpenForm 注文追加登録モーダルを開く
 * @param setIsOpenConfirm 注文確定モーダルを開く
 * @param isEditMode 編集モーダル
 * @returns コンポーネント
 */
function HeaderBox(
  onBack: Function,
  setIsOpenOrderSearch: Dispatch<SetStateAction<boolean>>,
  setIsOpenForm: Dispatch<SetStateAction<boolean>>,
  setIsOpenConfirm: Dispatch<SetStateAction<boolean>>,
  isEditMode: boolean,
) {
  return (
    <Box sx={styles.buttonContainer}>
      <Box sx={styles.leftButtonContainer}>
        <Box mx={1}>
          <Button onClick={() => onBack()} variant="custom-outlined">
            <Box>＜ 戻る</Box>
          </Button>
        </Box>
      </Box>
      <Box mx={1}>
        <Button onClick={() => setIsOpenOrderSearch(true)} variant="contained">
          <Search />
          <Box>絞込検索</Box>
        </Button>
      </Box>
      {!isEditMode && (
        <Box mx={1}>
          <Button onClick={() => setIsOpenForm(true)} variant="contained">
            <Box>注文追加登録</Box>
          </Button>
        </Box>
      )}
      <Box mx={1}>
        <Button onClick={() => setIsOpenConfirm(true)} variant="contained">
          <Box>注文確定</Box>
        </Button>
      </Box>
    </Box>
  );
}

/**
 * 本部用注文作成・編集画面
 * @returns JSX.Element
 */
function WrappedOrderCheckForm() {
  const [isOpenOrderSearch, setIsOpenOrderSearch] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [searchParams] = useSearchParams();
  const isEditMode = !(searchParams.get("mode") === "create"); // 確認画面が編集モードかどうか
  const isCheck = searchParams.get("check") === "true"; // 確認ボタンから画面に遷移したか
  const [isOpenForm, setIsOpenForm] = useState(!isEditMode ? !isCheck : true);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useContext(SuccessAlertContext);
  const searchParamShopCd = searchParams.get("shop-cd") ?? "";
  const initOrderNumberDetailed = isEditMode ? (searchParams.get("order-number") ?? undefined) : undefined; // 編集モードの場合にどの注文依頼を編集するか
  const [initOrderNumber, setInitOrderNumber] = useState(initOrderNumberDetailed ? [initOrderNumberDetailed?.split("-")[0], initOrderNumberDetailed?.split("-")[1], initOrderNumberDetailed?.split("-")[2]].join('-') : undefined);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | undefined>(initOrderNumber ?? undefined);
  const [shopCd, setShopCd] = useState(searchParamShopCd);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false); // 注文確定モーダルのloading
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState<number | undefined>(undefined);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false); // 注文確認画面からの削除モーダルのloading
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false); // 注文確認画面からの削除モーダル
  const [showBackAlert, setShowBackAlert] = useState(false);
  const [isBackClose, setIsBackClose] = useState(isEditMode ? false : isCheck); // 戻るボタンを押した時に注文作成画面を閉じるか1つ前の画面に戻るかのフラグ。

  // 検索条件のstate
  const [
    {
      isRepresentativeCd,
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      companyName,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
      category,
    },
    {
      setIsRepresentativeCd,
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCompanyName,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setjanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
    },
    {
      classificationOptions,
      tyreOptions,
      wheelOptions,
      brandOptions,
      wholeShopOptions: shopOptions,
    },
    { isLoading: isOptionsLoading },
  ] = useOrderSearch(true);

  const [
    {
      total,
      orderList,
      isLoading: isOrderLoading,
      isMoreLoading,
      isDestinationLoading,
      isFastLoading,
      fireMoreLoadEvent,
      destinationCheckData,
    },
    {
      setOrderList,
      get,
      getMore: getMoreOrderList,
      setFireMoreLoadEvent,
      getDestination,
      setDestinationCheckData,
      setInit,
      setTotal,
    },
  ] = useOrderForm(showAlert);
  const navigate = useNavigate();

  const representativeParam = () => {
    if (hasRepresentativeCd.indexOf(isRepresentativeCd) === 2 ) return 0
    if (hasRepresentativeCd.indexOf(isRepresentativeCd) === 1 ) return 1
    return undefined;
  }

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (initONum?: string): SearchFormApiParams => {
    const params: Omit<SearchFormApiParams, "limit" | "offset"> = {
      screen_type: 21,
      sort_type: 2,
      order_build_no: isEditMode && (initONum || initOrderNumber) ? (initONum ?? initOrderNumber) : undefined,
      order_no: orderNumber,
      is_customer_order: isEditMode ? undefined : (isCustomerOrder !== undefined ? Number(isCustomerOrder) : undefined),
      is_representative: isEditMode ? undefined : representativeParam(),
      order_status: isEditMode
        ? '1,101'
        : '100',
      store_name: shopName,
      author: storeOrderer,
      approver,
      // order_num: orderNumber,
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: janCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
      vendor_cd: supplier
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "",
      order_date_start: convertUTCToDate(orderStartDate),
      order_date_end: convertUTCToDate(orderEndDate),
    };
    return removeEmptyValues(params);
  };

  const handleSearch = async () => {
    setIsOpenOrderSearch(false);
    await get(createGetListApiParams());
  };

  const handleEdit = (index: number) => {
    const oNum = orderList[index].orderNumber;
    setSelectedOrderNumber([oNum.split("-")[0], oNum.split("-")[1], oNum.split("-")[2]].join('-'));
    setIsOpenForm(true);
  };

  const handleDelete = async (index: number) => {
    setSelectedDeleteIndex(index);
    setIsOpenDeleteModal(true);
  };

  const deleteBill = async () => {
    if (selectedDeleteIndex === undefined) {
      showAlert("削除する注文を選択してください。", "error");
      return;
    }
    const o = orderList[selectedDeleteIndex];
    const params: RegisterBillParam = {
      // client_cd: shopCd,
      is_delete: 1,
      order_list: [removeEmptyValues({
        order_build_no: o.orderNumber, // 削除が目的なのでorderNumberさえあればよいが、一応doc通り入れておく。
        item_cd: o.productCd,
        jan_cd: o.janCd,
        item_name: o.productName,
        order_quantity: o.orderCount,
        unit_base_price: o.headquartersUnitCost,
        unit_cost_price: o.wholesaleUnitPrice,
        unit_sale_price: o.shopUnitPrice,
        is_set_order: 'isSet' in o ? Number(o.isSet) : 0,
        is_staggered: 'isStaggered' in o ? Number(o.isStaggered) : 0,
        is_customer_order: Number(o.isCustomerOrder),
        is_direct_shipping: Number(o.isDirectOrder),
        author: o.orderCreator,
        approver: o.orderApprover,
        client_doc_no: '',
        desired_date: o.deliveryDate,
        shipping_scheduled: '',
        shipping_dest_code: '',
        shipping_dest_zip: '',
        shipping_dest_address: '',
        shipping_dest_tel: '',
        // order_store_fax: '',
        shipping_dest_name: '',
        memo: o.shopRemarks,
        remarks: o.headquartersRemarks,
      })],
    }
    setIsDeleteLoading(true);
    try {
      await registerBillApi(params);
      showAlert("注文を削除しました。", "success");
    } catch (err) {
      console.error(err);
      showAlert("注文の削除に失敗しました。", "error");
    } finally {
      setIsDeleteLoading(false);
      setIsOpenDeleteModal(false);
    }
  };

  const handleBack = () => navigate(-1);
  const tableHeadElement = () => (
    <>
      <TableRow key={1}>
        <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
          編集
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>注文番号</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>商品コード</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>JANコード</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>ステータス</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>仕入先名</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>大分類</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>ブランド</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol} colSpan={2}>
          商品名
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>サイズ</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>品番</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>客注</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierTopCol}>直送</TableHeaderCellNarrow>
        <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={1}>
          確定時<br />エラー判定
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow center sx={styles.supplierTopCol} rowSpan={2}>
          削除
        </TableHeaderCellNarrow>
      </TableRow>
      <TableRow key={2}>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文日</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>納品希望日</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>
          仕入先
          <br />
          入荷予定
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          発注数
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          本部原単価
          {/* <br />
          (単価) */}
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          仕切単価
          {/* <br />
          (単価) */}
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow right sx={styles.supplierBottomCol}>
          店舗売単価
          {/* <br />
          (単価) */}
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文書作成者</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>注文書承認者</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol}>店舗名</TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol} colSpan={2}>
          店舗備考
        </TableHeaderCellNarrow>
        <TableHeaderCellNarrow sx={styles.supplierBottomCol} colSpan={2}>
          本部備考
        </TableHeaderCellNarrow>
      </TableRow>
    </>
  );
  const tableBodyElement = () => (
    <>
      {orderList.map((order, index) => {
        const isZebraStripeRow = index % 2 === 1;
        return (
          <Fragment key={order.id}>
            <TableRow key={1}>
              <TableCellNarrow center multi gray={isZebraStripeRow} rowSpan={2}>
                <Box sx={{ cursor: "pointer" }} onClick={() => handleEdit(index)}>
                  <EditIcon />
                </Box>
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderPlainNumber || ''}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.productCd}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.janCd}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderStatus === orderStatusOptionList[8] ? orderStatusOptionList[1] : (order.orderStatus === orderStatusOptionList[9] ? orderStatusOptionList[2] : order.orderStatus)}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.supplierName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.classification}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.brand}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                {order.productName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.size}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.productNumber}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.isCustomerOrder ? "〇" : "×"}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.isDirectOrder ? "〇" : "×"}
              </TableCellNarrow>
              <TableCellNarrow center multi gray={isZebraStripeRow} rowSpan={1}>
                {order.confirmErrMsg}
              </TableCellNarrow>
              <TableCellNarrow center multi gray={isZebraStripeRow} rowSpan={2}>
                <Box sx={{ cursor: "pointer" }} onClick={() => handleDelete(index)}>
                  <DeleteIcon />
                </Box>
              </TableCellNarrow>
            </TableRow>
            <TableRow key={2}>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.deliveryDate}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.arrivalDate}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {localeStringDisZero(order.orderCount)}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {/* {localeStringDisZero(order.headquartersCost)}
                <br /> */}
                {`${localeStringDisZero(order.headquartersUnitCost)}`}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {/* {localeStringDisZero(order.wholesalePrice)}
                <br /> */}
                {`${localeStringDisZero(order.wholesaleUnitPrice)}`}
              </TableCellNarrow>
              <TableCellNarrow right multi gray={isZebraStripeRow}>
                {/* {localeStringDisZero(order.shopPrice)}
                <br /> */}
                {`${localeStringDisZero(order.shopUnitPrice)}`}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderCreator}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.orderApprover}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow}>
                {order.shopName}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                {order.shopRemarks}
              </TableCellNarrow>
              <TableCellNarrow multi gray={isZebraStripeRow} colSpan={2}>
                {order.headquartersRemarks}
              </TableCellNarrow>
            </TableRow>
          </Fragment>
        );
      })}
    </>
  );

  // スクロール処理
  const handleScroll = async (e: UIEvent<HTMLDivElement>) => {
    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current || isMoreLoading) {
      return;
    }
    // const scrollTop = window.scrollY;
    // const windowHeight = window.innerHeight;
    // const documentHeight = document.documentElement.scrollHeight;
    const { scrollTop } = e.currentTarget;
    const windowHeight = e.currentTarget.clientHeight;
    const documentHeight = e.currentTarget.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      console.log("scroll event:", scrollTop + documentHeight, windowHeight, documentHeight);
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  // 注文作成・編集モーダルが編集モードかどうか（確認画面が編集モードかどうかには関係ない）
  const checkEditModeModal = () => !!selectedOrderNumber;

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!localEnv && !(userType === "C0001" || userType === "C0005")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "本部ユーザーでログインしてください。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (!localEnv && checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else {
          await get(createGetListApiParams());
          setInit(true);
        }
      })();
    }, 0);
    // window.addEventListener('scroll', handleScroll);
    // return () => {
    //   window.removeEventListener('scroll', handleScroll);
    // }
  }, []);

  // ローディングの統一
  useEffect(() => {
    setIsLoading(isOrderLoading || isOptionsLoading);
  }, [isOrderLoading, isOptionsLoading]);

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await getMoreOrderList(createGetListApiParams());
    })();
  }, [fireMoreLoadEvent]);

  return (
    <>
      <OrderContainer
        orderTitle="注文確認"
        OrderTable={
          <OrderTable
            searchLen={total}
            isAllLoaded={orderList.length >= total}
            tableHeadElement={() => tableHeadElement()}
            tableBodyElement={() => tableBodyElement()}
            isLoading={isLoading}
            isMoreLoading={isMoreLoading}
            handleScroll={handleScroll}
            limit={0}
            total={0}
            orderListLength={0}
            isFastLoading={isFastLoading}
            getMoreOrderList={getMoreOrderList}
            createGetListApiParams={createGetListApiParams}
          />
        }
        HeaderBox={HeaderBox(
          handleBack,
          setIsOpenOrderSearch,
          setIsOpenForm,
          setIsOpenConfirm,
          isEditMode,
        )}
        isLoading={isLoading}
        helpPath="/resources/orders/manual_order_product_check.pdf"
      />
      {!isLoading && (
        <OrderSearchDialog
          open={isOpenOrderSearch}
          onClose={() => setIsOpenOrderSearch(false)}
          onSearch={handleSearch}
          isRepresentativeCd={isRepresentativeCd}
          isCustomerOrder={isCustomerOrder}
          shopName={shopName}
          orderStatus={orderStatus}
          orderReceiptStatus={orderReceiptStatus}
          storeOrderer={storeOrderer}
          approver={approver}
          orderStartDate={orderStartDate}
          orderEndDate={orderEndDate}
          companyCd={companyCdField}
          companyName={companyName}
          centerCd={centerCd}
          orderNumber={orderNumber}
          productCd={productCd}
          productName={productName}
          brand={brand}
          janCd={janCd}
          productNumber={productNumber}
          tyreWidth={tyreWidth}
          tyreRatio={tyreRatio}
          tyreRimDiam={tyreRimDiam}
          wheelWidth={wheelWidth}
          wheelPcd={wheelPcd}
          wheelHole={wheelHole}
          wheelRimDiam={wheelRimDiam}
          classification={classification}
          supplier={supplier}
          tyreOptions={tyreOptions}
          wheelOptions={wheelOptions}
          classificationOptions={classificationOptions}
          brandOptions={brandOptions}
          setIsRepresentativeCd={setIsRepresentativeCd}
          setIsCustomerOrder={setIsCustomerOrder}
          setShopName={setShopName}
          setOrderStatus={setOrderStatus}
          setOrderReceiptStatus={setOrderReceiptStatus}
          setStoreOrderer={setStoreOrderer}
          setApprover={setApprover}
          setOrderStartDate={setOrderStartDate}
          setOrderEndDate={setOrderEndDate}
          setCompanyCd={setCompanyCdField}
          setCompanyName={setCompanyName}
          setCenterCd={setCenterCd}
          setProductCd={setProductCd}
          setOrderNumber={setOrderNumber}
          setProductName={setProductName}
          setBrand={setBrand}
          setjanCd={setjanCd}
          setProductNumber={setProductNumber}
          setTyreWidth={setTyreWidth}
          setTyreRatio={setTyreRatio}
          setTyreRimDiam={setTyreRimDiam}
          setWheelWidth={setWheelWidth}
          setWheelPcd={setWheelPcd}
          setWheelHole={setWheelHole}
          setWheelRimDiam={setWheelRimDiam}
          setClassification={setClassification}
          setSupplier={setSupplier}
          setCategory={setCategory}
          screenType={isEditMode ? "本部編集" : "本部作成"}
        />
      )}
      <OrderEditForm
        open={isOpenForm}
        title={isEditMode ? "注文編集" : "注文作成"}
        onClose={async (initONum?: string) => {
          setIsOpenForm(false);
          setSelectedOrderNumber(undefined);
          get(createGetListApiParams(initONum));
          setIsBackClose(true); // 注文作成・編集モーダルを閉じたら、戻るボタンを押した時に注文作成画面を閉じるようにする
        }}
        orderInitList={[]}
        isEditMode={isEditMode}
        isEditModeModal={checkEditModeModal()}
        selectedOrderNumber={selectedOrderNumber}
        setSelectedOrderNumber={setSelectedOrderNumber}
        setInitOrderNumber={setInitOrderNumber}
        shopCd={shopCd}
        setShopCd={setShopCd}
        shopOptions={shopOptions}
        getDestination={getDestination}
        destinationCheckData={destinationCheckData}
        setDestinationCheckData={setDestinationCheckData}
        isDestinationLoading={isDestinationLoading}
        classificationOptions={classificationOptions}
        brandOptions={brandOptions}
        tyreOptions={tyreOptions}
        wheelOptions={wheelOptions}
        get={get}
        onBack={() => setShowBackAlert(true)}
        wholeSupplierOptions={extractNoDupplicationSupplier(classificationOptions)}
        isOptionsLoading={isOptionsLoading}
        isBackClose={isBackClose}
        navigate={navigate}
      />

      <BackAlert
        open={showBackAlert}
        onBack={() => navigate(-1)}
        onClose={() => setShowBackAlert(false)}
      />

      {/* 注文確定確認モーダル */}
      <Dialog
        open={isOpenConfirm}
        onClose={() => {
          setIsOpenConfirm(false);
        }}
        PaperProps={{ sx: { position: "relative" } }}
      >
        <DialogTitle>注文確定</DialogTitle>
        <Box ml={2} mt={1}>
          <DialogContent>
            現在の絞込検索条件に該当する全ての注文を確定します。
            注文を確定後は変更、キャンセルができません。
            <br />
            <br />
            よろしいですか？
          </DialogContent>
          {/* <DialogContent>よろしいですか？</DialogContent> */}
        </Box>
        <DialogActions>
          <Button
            variant="custom-outlined"
            onClick={() => {
              setIsOpenConfirm(false);
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setIsConfirmLoading(true);
              try {
                // const params: OrderConfirmParams = {
                //   client_cd: shopCd,
                //   // order_list: orderList.map((o: OrderForm) =>
                //   //    ({
                //   //     item_cd: o.productCd,
                //   //     jan_Cd: o.janCd,
                //   //     item_name: o.productName,
                //   //     order_quantity: o.orderCount,
                //   //     unit_base_price: o.headquartersUnitCost,
                //   //     unit_cost_price: o.wholesaleUnitPrice,
                //   //     unit_sale_price: o.shopUnitPrice,
                //   //     is_set_order: o.isSet,
                //   //     is_customer_order: o.isCustomerOrder,
                //   //     is_request_to_admin: false,
                //   //     is_direct_shipping: o.isDirectOrder,
                //   //     author: o.orderCreator,
                //   //     approver: o.orderApprover,
                //   //     client_doc_no: o.clientBillNumber,
                //   //     desired_date: o.deliveryDate,
                //   //     shipping_scheduled: o.expectedShippingDate,
                //   //     shipping_dest_cd: shippingDestination,
                //   //     shipping_dest_zip: postalCd,
                //   //     shipping_dest_address: destinationAddress,
                //   //     shipping_dest_tel: phoneNumber,
                //   //     shipping_dest_fax: faxNumber,
                //   //     shipping_dest_name: destinationName,
                //   //     memo: o.shopRemarks,
                //   //     remark: o.headquartersRemarks,
                //   //   })
                //   // ),
                // };
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const {screen_type, sort_type, ...params}: Omit<SearchFormApiParams, "limit" | "offset"> = createGetListApiParams();
                const msg = await confirmOrderApi(removeEmptyValues(params));
                setIsOpenConfirm(false);
                Notify.setNotification({
                  show: true,
                  title: msg, // 確定処理をバッチで処理するためその旨を伝える
                  confirmLabel: "OK",
                  actionsPosition: "end",
                });
                // showAlert("注文を確定しました。", "success");
                Notify.onClose = () => {
                  // 取得し直し
                  get(createGetListApiParams());
                };
              } catch (err) {
                const error = err as AxiosError;
                console.error(err);
                setTimeout(() => {
                  // レスポンス自体がなくエラーになった時
                  if (!error.response) {
                    Notify.setNotification({
                      show: true,
                      title: "APIに接続できませんでした。",
                      confirmLabel: "OK",
                      actionsPosition: "end",
                    });
                    return;
                  }
                  const { status } = error.response;
                  const data = error.response.data as OrderConfirmErrorResponseApi;
                  switch (status) {
                    case 400:
                      Notify.setNotification({
                        show: true,
                        title: "パラメータエラーです。",
                        confirmLabel: "OK",
                        actionsPosition: "end",
                      });
                      break;
                    case 422:
                      Notify.setNotification({
                        show: true,
                        title: `入力エラーがあります。`,
                        message: data.messages.join("\n"),
                        titleColor: "var(--error)",
                      });
                      break;
                    default:
                      break;
                  }
                }, 0);
              } finally {
                setIsConfirmLoading(false);
              }
            }}
            disabled={isConfirmLoading}
          >
            注文確定
          </Button>
        </DialogActions>
        {isConfirmLoading && ( // マスク
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.2)",
              zIndex: (theme) => theme.zIndex.modal + 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Dialog>

      {/* 注文削除確認モーダル */}
      <Dialog
        open={isOpenDeleteModal}
        onClose={() => {
          setIsOpenDeleteModal(false);
        }}
        PaperProps={{ sx: { position: "relative" } }}
      >
        <DialogTitle>注文削除</DialogTitle>
        <DialogContent>
          <Box ml={2} mt={1}>
            <DialogContent>
              注文を削除します。
              <br />
              <br />
              よろしいですか？
            </DialogContent>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="custom-outlined"
            onClick={() => {
              setIsOpenDeleteModal(false);
              setSelectedDeleteIndex(undefined);
            }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              await deleteBill();
              await get(createGetListApiParams());
            }}
          >
            削除
          </Button>
        </DialogActions>
        {isDeleteLoading && ( // マスク
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.2)",
              zIndex: (theme) => theme.zIndex.modal + 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Dialog>
    </>
  );
}

/**
 * サイドメニューでクエリパラメータが変わった場合に強制的にkeyでリロードさせる
 * @returns JSX.Element
 */
export default function OrderCheckForm() {
  const {search} = useLocation(); // modeがcreate, editなどサイドメニューから変わった際に再読み込みする
  const [key, setKey] = useState(0);
  useEffect(() => {
    console.log("search:", search)
    setKey(k => k + 1);
  },[search])
  return <WrappedOrderCheckForm key={key} />;
}
